import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Sparkles } from 'lucide-react';
import { varianceNarrativeApi } from '@/lib/varianceNarrative';
import { isPlanGateError } from '@/lib/planGate';
import { UpgradeWall } from '@/components/app/UpgradeWall';
import { MarkdownText } from '@/components/MarkdownText';
import { useToast, errMessage } from '@/components/Toast';

function lastMonthRange(): { start: string; end: string } {
  const now = new Date();
  const firstThis = new Date(now.getFullYear(), now.getMonth(), 1);
  const start = new Date(firstThis.getFullYear(), firstThis.getMonth() - 1, 1);
  const end = new Date(firstThis.getFullYear(), firstThis.getMonth(), 0);
  const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  return { start: iso(start), end: iso(end) };
}

/**
 * Autonomous variance narrative (W2-2). Generates a board-ready budget-vs-actual
 * summary for a period — ranked drivers, cited to the facts behind them —
 * rendered through the editorial MarkdownText. Growth+; UpgradeWall on the gate.
 */
export function VarianceNarrative({ wsId }: { wsId: string }) {
  const { t } = useTranslation();
  const toast = useToast();
  const init = lastMonthRange();
  const [periodStart, setPeriodStart] = useState(init.start);
  const [periodEnd, setPeriodEnd] = useState(init.end);
  const [open, setOpen] = useState(false);

  const gen = useMutation({
    mutationFn: () => varianceNarrativeApi.narrate(wsId, { periodStart, periodEnd }),
    onError: (e) => { if (!isPlanGateError(e)) toast.error(errMessage(e, t('variance_narrative.error', { defaultValue: 'Could not generate the narrative.' }))); },
  });
  const planGated = gen.error && isPlanGateError(gen.error);
  const result = gen.data;

  return (
    <div className="rounded-2xl border border-ink-200 bg-white p-5 mb-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h3 className="text-sm font-semibold text-ink-700 inline-flex items-center gap-1.5">
          <Sparkles className="h-4 w-4 text-emerald-600" />
          {t('variance_narrative.title', { defaultValue: 'AI variance narrative' })}
        </h3>
        <div className="flex items-end gap-2 flex-wrap">
          <input type="date" value={periodStart} onChange={(e) => setPeriodStart(e.target.value)} aria-label={t('signals.period_start', { defaultValue: 'Period start' })} className="rounded-md border border-ink-200 px-2 py-1.5 text-xs" />
          <input type="date" value={periodEnd} onChange={(e) => setPeriodEnd(e.target.value)} aria-label={t('signals.period_end', { defaultValue: 'Period end' })} className="rounded-md border border-ink-200 px-2 py-1.5 text-xs" />
          <button
            onClick={() => { setOpen(true); gen.mutate(); }}
            disabled={gen.isPending}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-emerald-600 text-white text-xs font-semibold hover:bg-emerald-700 disabled:opacity-60"
          >
            <Sparkles className="h-3.5 w-3.5" />
            {gen.isPending ? t('variance_narrative.generating', { defaultValue: 'Generating…' }) : t('variance_narrative.generate', { defaultValue: 'Generate narrative' })}
          </button>
        </div>
      </div>

      {open && (
        <div className="mt-3">
          {planGated ? (
            <UpgradeWall wsId={wsId} requiredTier="growth" featureName={t('variance_narrative.feature_name', { defaultValue: 'AI variance narrative' })} />
          ) : gen.isPending ? (
            <p className="text-sm text-ink-500">{t('variance_narrative.generating', { defaultValue: 'Generating…' })}</p>
          ) : result ? (
            <div className="rounded-xl bg-ink-50 border border-ink-200 p-4">
              <div className="text-[11px] text-emerald-700 font-semibold mb-1.5">
                ✦ {t('variance_narrative.byline', { defaultValue: 'CadencEZ AI · board-ready' })}
                {result.citedFactIds.length > 0 && (
                  <span className="ml-2 text-ink-500 font-normal">· {t('variance.cites_n', { count: result.citedFactIds.length })}</span>
                )}
              </div>
              <MarkdownText>{result.narrative}</MarkdownText>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}
